/**
 * Pestaña "Planos Generados" dentro de Ficha FFE.
 * Lista los PlanoGenerado del proyecto (todos los recuadros editados),
 * permite previsualizar, re-editar y descargar todo el conjunto en PDF.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import { UPLOAD_URL } from '../../config';
import { getTipoPlano, ordenTipoPlano } from './tiposPlano';
import './PlanosGeneradosTab.css';

const PlanosGeneradosTab = ({ cotizacion, planos: planosFFE, onCambio }) => {
  const { success, error: showError } = useNotification();
  const [planosGen, setPlanosGen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [descargandoPDF, setDescargandoPDF] = useState(false);
  const [editandoNombre, setEditandoNombre] = useState(null); // id del plano en edicion

  // Avisa a la Ficha FFE (misma pestaña o el editor en otra ventana) que los
  // planos cambiaron, para que refresque nombres e imagenes sin recargar.
  const avisarCambio = () => {
    // Aviso directo al componente padre (Ficha FFE): es el camino fiable dentro
    // de la misma pagina. El canal queda para las OTRAS ventanas (el editor).
    if (typeof onCambio === 'function') onCambio();
    try {
      const cid = cotizacion?.id || null;
      if ('BroadcastChannel' in window) {
        const ch = new BroadcastChannel('tuvi-planos');
        ch.postMessage({ tipo: 'plano-guardado', cotizacion_id: cid });
        // Cerrarlo de inmediato puede descartar el mensaje en algunos navegadores.
        setTimeout(() => { try { ch.close(); } catch { /* ya cerrado */ } }, 1500);
      }
      localStorage.setItem('tuvi_plano_guardado', JSON.stringify({ cotizacion_id: cid, ts: Date.now() }));
    } catch { /* si falla el aviso no pasa nada grave */ }
  };

  const cargar = async () => {
    if (!cotizacion?.id) return;
    try {
      setLoading(true);
      const data = await client.query(
        `query($cid: Int!) {
          planosGenerados(cotizacion_id: $cid) {
            id recuadro_uuid url_index tipo_plano aplica hoja titulo
            imagen_final_url updated_at
          }
        }`,
        { cid: cotizacion.id }
      );
      setPlanosGen(data.planosGenerados || []);
    } catch (e) {
      showError('Error cargando planos: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [cotizacion?.id]);

  // El editor de planos se abre en OTRA ventana, asi que esta pestaña no se
  // enteraba de los cambios (habia que salir y volver a entrar). Ahora refresca
  // cuando el editor avisa que guardo, y tambien al volver el foco a la pestaña.
  useEffect(() => {
    if (!cotizacion?.id) return undefined;
    const mismaCotizacion = (cid) => !cid || Number(cid) === Number(cotizacion.id);

    let canal = null;
    if ('BroadcastChannel' in window) {
      canal = new BroadcastChannel('tuvi-planos');
      canal.onmessage = (e) => {
        if (e.data?.tipo === 'plano-guardado' && mismaCotizacion(e.data.cotizacion_id)) cargar();
      };
    }
    const onStorage = (e) => {
      if (e.key !== 'tuvi_plano_guardado' || !e.newValue) return;
      try { if (mismaCotizacion(JSON.parse(e.newValue).cotizacion_id)) cargar(); } catch { /* ignorar */ }
    };
    const onFocus = () => { if (document.visibilityState === 'visible') cargar(); };

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      if (canal) canal.close();
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
    /* eslint-disable-next-line */
  }, [cotizacion?.id]);

  // Helpers: nombre del recuadro a partir del UUID (mirando planosFFE)
  const recuadroTitulo = useMemo(() => {
    const map = {};
    (planosFFE || []).forEach(p => { if (p.id) map[p.id] = p.titulo; });
    return map;
  }, [planosFFE]);

  const reeditar = (p) => {
    const titulo = recuadroTitulo[p.recuadro_uuid] || 'Plano';
    const ffePlano = (planosFFE || []).find(x => x.id === p.recuadro_uuid);
    const imagenBase = ffePlano?.urls?.[p.url_index] || '';
    const params = new URLSearchParams({
      cotizacion_id: cotizacion.id,
      recuadro_uuid: p.recuadro_uuid,
      url_index: p.url_index,
      titulo,
      proyecto_nombre: cotizacion?.proyecto_nombre || '',
      apartamento: cotizacion?.apartamento || '',
      imagen_base: imagenBase,
    });
    window.open(`/editor-planos?${params.toString()}`, '_blank');
  };

  const eliminar = async (p) => {
    if (!window.confirm('¿Eliminar este plano generado? La imagen y el editor se perderán.')) return;
    try {
      await client.mutate(`mutation($id: Int!) { eliminarPlanoGenerado(id: $id) { success message } }`, { id: p.id });
      success('Plano eliminado');
      cargar();
      avisarCambio();
    } catch (e) {
      showError('Error: ' + e.message);
    }
  };

  const toggleNA = async (p) => {
    try {
      await client.mutate(`mutation($id: Int!, $a: Boolean!) { marcarAplicaPlano(id: $id, aplica: $a) { success message } }`, { id: p.id, a: !p.aplica });
      cargar();
      avisarCambio();
    } catch (e) {
      showError('Error: ' + e.message);
    }
  };

  // Guarda el nombre de la hoja. Vacio = vuelve al nombre del tipo de plano.
  const guardarNombre = async (p, valor) => {
    setEditandoNombre(null);
    const limpio = (valor || '').trim();
    if (limpio === (p.titulo || '')) return;      // no cambio nada
    try {
      await client.mutate(
        `mutation($id: Int!, $t: String) { renombrarPlanoGenerado(id: $id, titulo: $t) { success message } }`,
        { id: p.id, t: limpio || null }
      );
      cargar();
      avisarCambio();
    } catch (e) {
      showError('No se pudo cambiar el nombre: ' + e.message);
    }
  };

  const descargarPDF = async () => {
    if (!cotizacion?.id) return;
    if (planosGen.filter(p => p.aplica).length === 0) {
      showError('No hay planos aplicables para incluir en el PDF');
      return;
    }
    try {
      setDescargandoPDF(true);
      const token = localStorage.getItem('tuvi_token');
      const resp = await fetch(`${UPLOAD_URL}/planos-pdf/${cotizacion.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.message || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `planos_${(cotizacion.proyecto_nombre || 'proyecto').replace(/\s+/g, '_')}_apto_${cotizacion.apartamento || ''}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      success('PDF descargado');
    } catch (e) {
      showError('Error: ' + e.message);
    } finally {
      setDescargandoPDF(false);
    }
  };

  // URL completa de imagen final (firmada por el resolver al guardar)
  const imgUrl = (p) => {
    if (!p.imagen_final_url) return null;
    if (p.imagen_final_url.startsWith('http')) return p.imagen_final_url;
    // Es un key del bucket planos-generados
    return `${UPLOAD_URL}/catalogo/img/planos-generados/${p.imagen_final_url}`;
  };

  // Ordenar en el orden del catálogo (TIPOS_PLANO) y luego por hoja
  const planosOrdenados = useMemo(() => {
    return [...planosGen].sort((a, b) =>
      ordenTipoPlano(a.tipo_plano) - ordenTipoPlano(b.tipo_plano) || (a.hoja || 0) - (b.hoja || 0));
  }, [planosGen]);

  if (loading) return <div className="pg-loading">Cargando planos generados...</div>;

  return (
    <div className="pg-root">
      <div className="pg-header">
        <div>
          <h3>Planos Generados</h3>
          <p className="pg-sub">
            {planosGen.length} plano(s) · {planosGen.filter(p => p.aplica).length} aplicables
          </p>
        </div>
        <button
          className="pg-btn pg-btn-primary"
          onClick={descargarPDF}
          disabled={descargandoPDF || planosGen.filter(p => p.aplica).length === 0}
        >
          {descargandoPDF ? 'Generando PDF...' : '📥 Descargar todos en PDF'}
        </button>
      </div>

      {planosGen.length === 0 ? (
        <div className="pg-empty">
          <p>Aún no hay planos generados.</p>
          <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
            Ve a la pestaña <strong>Ficha FFE</strong>, selecciona una imagen en cualquier recuadro de plano
            y presiona <strong>🎨 Editar plano</strong>.
          </p>
        </div>
      ) : (
        <div className="pg-cards">
          {planosOrdenados.map(p => {
            const tipo = getTipoPlano(p.tipo_plano);
            const url = imgUrl(p);
            return (
              <div key={p.id} className={`pg-card ${!p.aplica ? 'pg-card-na' : ''}`}>
                {url ? (
                  <img src={url} alt={tipo?.nombre} className="pg-card-img" />
                ) : (
                  <div className="pg-card-noimg">Sin imagen</div>
                )}
                <div className="pg-card-body">
                  {editandoNombre === p.id ? (
                    <input
                      className="pg-card-tipo-input"
                      autoFocus
                      defaultValue={p.titulo || ''}
                      placeholder={tipo?.nombre || p.tipo_plano}
                      onBlur={(e) => guardarNombre(p, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.target.blur();
                        if (e.key === 'Escape') setEditandoNombre(null);
                      }}
                    />
                  ) : (
                    <div
                      className="pg-card-tipo pg-card-tipo-edit"
                      title="Clic para cambiar el nombre"
                      onClick={() => setEditandoNombre(p.id)}
                    >
                      {p.titulo || tipo?.nombre || p.tipo_plano}
                      <span className="pg-edit-icon">✎</span>
                    </div>
                  )}
                  {p.titulo && <div className="pg-card-subtipo">{tipo?.nombre || p.tipo_plano}</div>}
                  {!p.aplica && <span className="pg-badge-na">N/A</span>}
                </div>
                <div className="pg-card-actions">
                  {url && <a className="pg-btn pg-btn-ghost" href={url} target="_blank" rel="noopener noreferrer">👁 Ver</a>}
                  <button className="pg-btn pg-btn-ghost" onClick={() => reeditar(p)}>🎨 Re-editar</button>
                  <button className="pg-btn pg-btn-ghost" onClick={() => setEditandoNombre(p.id)}>✎ Renombrar</button>
                  <button className="pg-btn pg-btn-ghost" onClick={() => toggleNA(p)}>
                    {p.aplica ? 'Marcar N/A' : 'Reactivar'}
                  </button>
                  <button className="pg-btn pg-btn-danger" onClick={() => eliminar(p)}>🗑</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlanosGeneradosTab;
